import { useUIStore } from '@/config/stores/ui-store';
import {
  createConversation,
  deleteConversation,
  leaveConversation,
  updateConversation
} from '@/features/conversation/services/conversation.service';
import { updateConversationInCache } from '@/features/messages/utils/conversation-cache';
import { confirmMes } from '@/shared/utilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import type { Conversation, ConversationType } from '../models/conversation';
import { conversationKeys } from './conversation-keys';

export const useCreateConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createConversation,
    onSuccess: () => {
      toast.success(i18next.t('conversations.toasts.createSuccess'));
    },
    onSettled: () => {
      // Invalidates all conversation queries including list and search results
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    }
  });
};

export const useLeaveConversation = () => {
  const queryClient = useQueryClient();
  const { setActiveConversation } = useUIStore();

  const { mutateAsync } = useMutation({
    mutationFn: leaveConversation,
    onSuccess: () => {
      // Invalidates all conversation queries including list and search results
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    }
  });

  return {
    leave: async (memberId: string) => {
      const res = await confirmMes({
        title: i18next.t('conversations.leave.dialogTitle'),
        description: i18next.t('conversations.leave.description')
      });
      if (res) {
        toast.promise(() => mutateAsync(memberId), {
          pending: i18next.t('conversations.delete.deleting'),
          success: i18next.t('conversations.toasts.removeMemberSuccess')
        });
        setActiveConversation(null);
      }
    }
  };
};

export const useDeleteConversation = () => {
  const { setActiveConversation } = useUIStore();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => {
      setActiveConversation(null);
    }
  });
  return {
    xoa: async (convId: string, conversationType: ConversationType) => {
      const res = await confirmMes({
        title:
          conversationType === 'DIRECT'
            ? i18next.t('conversations.leave.dialogTitleSingle')
            : i18next.t('conversations.delete.dialogTitle'),
        description:
          conversationType === 'DIRECT'
            ? i18next.t('conversations.leave.descriptionSingle')
            : i18next.t('conversations.delete.description')
      });
      if (res) {
        await mutateAsync(convId);
      }
    },
    isPending
  };
};

export const useUpdateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: string; data: Conversation }) =>
      updateConversation(conversationId, data),
    onSuccess: (_, { conversationId, data: updatedData }) => {
      updateConversationInCache(queryClient, conversationId, updatedData);
    },
    onError: (error) => {
      console.error('Failed to update conversation:', error);
      toast.error(i18next.t('conversations.toasts.updateError'));
    }
  });
};
